// Item core (A2, monorail-yqv — plan amoeba-environment-and-drops.md).
// Rolls, affix pools, and the D2 name grammar:
//
//   uncommon  1 affix   "[Prefix] Base" / "Base of [Suffix]" — the name IS the affix
//   rare      2 affixes "The [Adjective] [Noun]" — evocative, affixes shown plainly
//   pristine  2 affixes at max magnitude — rare's grammar, star treatment in UI
//   unique    handcrafted rule-breakers (A6, monorail-kcs) — never rolled here
//
// No common tier: nothing droppable is trash (Hades rule). Rarity = magnitude,
// not mechanics; mechanical novelty is reserved for uniques.
//
// Affix values fold into player.mods via applyItemAffixes (called from
// recomputeMods) — systems never read items directly. Every affix maps to
// one unconditional line on the stats card (A4).

import type { Entity, ItemInstance, ItemSlot, Rarity } from './components'
import type { Mods } from './mods'
import { WEAPON_DEFS, type WeaponId } from './weapons'
import { T } from './tunables'

// ── Hat bases ───────────────────────────────────────────────────────────────
// Mundane objects, played straight. Purely a name + future render hook — all
// hat behavior comes from affixes.

export type HatId = 'bowler' | 'beret' | 'nightcap' | 'paper-hat' | 'tiny-crown'

export interface HatDef {
  id: HatId
  /** Voice-checked: a plausibly mundane object, presented as equipment. */
  name: string
  tint: [number, number, number]
}

export const HAT_DEFS: Record<HatId, HatDef> = {
  'bowler':     { id: 'bowler',     name: 'Bowler',     tint: [0.45, 0.4, 0.5] },
  'beret':      { id: 'beret',      name: 'Beret',      tint: [0.75, 0.35, 0.4] },
  'nightcap':   { id: 'nightcap',   name: 'Nightcap',   tint: [0.5, 0.6, 0.85] },
  'paper-hat':  { id: 'paper-hat',  name: 'Paper Hat',  tint: [0.9, 0.88, 0.8] },
  'tiny-crown': { id: 'tiny-crown', name: 'Tiny Crown', tint: [0.95, 0.85, 0.4] },
}

// Droppable weapon bases — the innate pseudopod is bare hands, never loot.
export const DROPPABLE_WEAPONS: WeaponId[] = ['bristle-whip', 'spit-gland']
export const ALL_HAT_IDS = Object.keys(HAT_DEFS) as HatId[]

// ── Affixes ─────────────────────────────────────────────────────────────────
// One roll per affix; pristine takes `max`. `word` is the D2 name fragment
// (prefix word, or the "of …" tail). ≥1/3 reference verbs/behaviors, not flat
// stats — that's where build identity lives with two slots.

export interface AffixDef {
  id: string
  slot: ItemSlot
  position: 'prefix' | 'suffix'
  /** Name fragment: the prefix word itself, or the suffix tail after "of". */
  word: string
  min: number
  max: number
  /** Short stat word for aligned display rows ("Guard", "Lifesteal").
   * Values are ALWAYS higher-is-better, so deltas compare cleanly. */
  label: string
  /** Unsigned magnitude for `v` in this affix's unit ("25%", "1.2u"). */
  fmt: (v: number) => string
  /** Fold the rolled value into the mods accumulator. */
  apply: (mods: Mods, v: number) => void
}

const pct = (v: number): string => `${Math.round(v * 100)}%`

export const AFFIX_DEFS: Record<string, AffixDef> = {
  // ── hat (6) ───────────────────────────────────────────────────────────────
  'tardigrade': {
    id: 'tardigrade', slot: 'hat', position: 'suffix', word: 'the Tardigrade',
    min: 0.15, max: 0.35,
    label: 'Integrity', fmt: pct,
    apply: (m, v) => { m.maxHpMult *= 1 + v },
  },
  'scooting': {
    id: 'scooting', slot: 'hat', position: 'suffix', word: 'Scooting',
    min: 0.08, max: 0.16,
    label: 'Locomotion', fmt: pct,
    apply: (m, v) => { m.speedMult *= 1 + v },
  },
  'padded': {
    id: 'padded', slot: 'hat', position: 'prefix', word: 'Padded',
    min: 0.12, max: 0.25,
    label: 'Guard', fmt: pct,
    apply: (m, v) => { m.damageTakenMult *= 1 - v },
  },
  'grabby': {
    id: 'grabby', slot: 'hat', position: 'prefix', word: 'Grabby',
    min: 0.5, max: 1.2,
    label: 'Magnet', fmt: v => `${v.toFixed(1)}u`,
    apply: (m, v) => { m.orbMagnetRange = Math.max(m.orbMagnetRange, v) },
  },
  'unremarkable': {
    id: 'unremarkable', slot: 'hat', position: 'suffix', word: 'the Unremarkable',
    min: 0.15, max: 0.3,
    label: 'Stealth', fmt: pct,
    apply: (m, v) => { m.detectionRadiusMult *= 1 - v },
  },
  'second-lunch': {
    id: 'second-lunch', slot: 'hat', position: 'suffix', word: 'the Second Lunch',
    min: 0.04, max: 0.1,
    label: 'Eat reach', fmt: v => `${v.toFixed(2)}u`,
    apply: (m, v) => { m.eatReachAdd += v },
  },
  // ── weapon (8) ────────────────────────────────────────────────────────────
  'cruel': {
    id: 'cruel', slot: 'weapon', position: 'prefix', word: 'Cruel',
    min: 0.2, max: 0.5,
    label: 'Attack', fmt: pct,
    apply: (m, v) => { m.attackDamageMult *= 1 + v },
  },
  'jagged': {
    id: 'jagged', slot: 'weapon', position: 'prefix', word: 'Jagged',
    min: 0.15, max: 0.35,
    label: 'Attack', fmt: v => v.toFixed(2),
    apply: (m, v) => { m.attackDamageAdd += v },
  },
  'brisk': {
    id: 'brisk', slot: 'weapon', position: 'prefix', word: 'Brisk',
    min: 0.15, max: 0.3,
    label: 'Cadence', fmt: pct,
    apply: (m, v) => { m.attackCooldownMult *= 1 - v },
  },
  'reaching': {
    id: 'reaching', slot: 'weapon', position: 'suffix', word: 'Reaching',
    min: 0.15, max: 0.35,
    label: 'Reach', fmt: pct,
    apply: (m, v) => { m.attackReachMult *= 1 + v },
  },
  'sniffles': {
    id: 'sniffles', slot: 'weapon', position: 'suffix', word: 'Sniffles',
    min: 0.15, max: 0.35,
    label: 'Lifesteal', fmt: pct,
    apply: (m, v) => { m.attackLifesteal += v },
  },
  'vacuole': {
    id: 'vacuole', slot: 'weapon', position: 'suffix', word: 'the Vacuole',
    min: 0.15, max: 0.3,
    label: 'Dash recharge', fmt: pct,
    apply: (m, v) => { m.dashCooldownMult *= 1 - v },
  },
  'spiteful': {
    id: 'spiteful', slot: 'weapon', position: 'prefix', word: 'Spiteful',
    min: 0.3, max: 0.7,
    label: 'Thorns', fmt: v => v.toFixed(1),
    apply: (m, v) => { m.thornsDamage += v },
  },
  'chaperone': {
    id: 'chaperone', slot: 'weapon', position: 'suffix', word: 'the Chaperone',
    min: 0.25, max: 0.6,
    label: 'Allies', fmt: pct,
    apply: (m, v) => { m.allyDamageMult *= 1 + v },
  },
}

export const ALL_AFFIX_IDS = Object.keys(AFFIX_DEFS)

// ── Rare-name word pools ────────────────────────────────────────────────────
// "The [Adjective] [Noun]" — evocative and unrelated to the affixes (D2 rares).
// Deadpan-observational vocabulary: things a tired archivist might name a find.

const RARE_ADJECTIVES = [
  'Reluctant', 'Polite', 'Sideways', 'Unlicensed', 'Damp', 'Adjacent',
  'Provisional', 'Punctual', 'Feral', 'Immodest', 'Quiet', 'Spare',
]
const RARE_NOUNS = [
  'Comma', 'Opinion', 'Drizzle', 'Meridian', 'Paperwork', 'Whisper',
  'Errand', 'Umlaut', 'Margin', 'Anthem', 'Pamphlet', 'Deposit',
]

// ── Rolling ─────────────────────────────────────────────────────────────────

let nextUid = 1
function uid(): string {
  // Timestamp + counter: unique across sessions without a UUID dependency.
  return `it-${Date.now().toString(36)}-${(nextUid++).toString(36)}`
}

function pick<T>(arr: T[], rng: () => number): T {
  return arr[Math.floor(rng() * arr.length)]
}

export function rollRarity(rng: () => number = Math.random): Exclude<Rarity, 'unique'> {
  const w = T.items.rarityWeights
  const r = rng() * (w.uncommon + w.rare + w.pristine)
  if (r < w.uncommon) return 'uncommon'
  if (r < w.uncommon + w.rare) return 'rare'
  return 'pristine'
}

function affixPool(slot: ItemSlot): AffixDef[] {
  return ALL_AFFIX_IDS.map(id => AFFIX_DEFS[id]).filter(a => a.slot === slot)
}

function baseName(item: Pick<ItemInstance, 'slot' | 'base'>): string {
  return item.slot === 'hat'
    ? HAT_DEFS[item.base as HatId].name
    : WEAPON_DEFS[item.base as WeaponId].name
}

/** The D2 grammar. Uncommon names state their affix; rare/pristine names are
 * evocative and the card states the affixes (A4). */
function generateName(
  slot: ItemSlot,
  base: string,
  rarity: Exclude<Rarity, 'unique'>,
  affixes: AffixDef[],
  rng: () => number,
): string {
  const b = baseName({ slot, base })
  if (rarity === 'uncommon') {
    const a = affixes[0]
    return a.position === 'prefix' ? `${a.word} ${b}` : `${b} of ${a.word}`
  }
  return `The ${pick(RARE_ADJECTIVES, rng)} ${pick(RARE_NOUNS, rng)}`
}

export interface RollOpts {
  rng?: () => number
  /** Force a rarity (drops usually let rollRarity decide). */
  rarity?: Exclude<Rarity, 'unique'>
  /** Force a base (e.g. cheat / guaranteed boss table). */
  base?: string
}

export function rollItem(slot: ItemSlot, opts: RollOpts = {}): ItemInstance {
  const rng = opts.rng ?? Math.random
  const rarity = opts.rarity ?? rollRarity(rng)
  const base = opts.base ?? (slot === 'hat'
    ? pick(ALL_HAT_IDS, rng)
    : pick(DROPPABLE_WEAPONS, rng))

  // Uncommon: 1 affix. Rare/pristine: 2 — one prefix + one suffix where the
  // pool allows, so informative names never double a position.
  const pool = affixPool(slot)
  const count = rarity === 'uncommon' ? 1 : 2
  const chosen: AffixDef[] = []
  if (count === 1) {
    chosen.push(pick(pool, rng))
  } else {
    const prefixes = pool.filter(a => a.position === 'prefix')
    const suffixes = pool.filter(a => a.position === 'suffix')
    chosen.push(pick(prefixes, rng), pick(suffixes, rng))
  }

  const affixes = chosen.map(a => ({
    id: a.id,
    value: rarity === 'pristine' ? a.max : a.min + (a.max - a.min) * rng(),
  }))

  return {
    id: uid(),
    slot,
    base,
    rarity,
    affixes,
    name: generateName(slot, base, rarity, chosen, rng),
  }
}

// ── Inventory + equip (entity-held; carried across respawn, persisted) ─────

export function addItem(player: Entity, item: ItemInstance): void {
  player.inventory ??= []
  player.inventory.push(item)
}

export function findItem(player: Entity, id: string): ItemInstance | undefined {
  return player.inventory?.find(i => i.id === id)
}

/** Equip an owned item into its slot (null clears the slot — bare hands /
 * bare head). No-op if the id isn't owned. */
export function equipItem(player: Entity, slot: ItemSlot, id: string | null): void {
  player.equipped ??= {}
  if (id === null) {
    player.equipped[slot] = undefined
    return
  }
  const item = findItem(player, id)
  if (!item || item.slot !== slot) return
  player.equipped[slot] = id
}

export function equippedItem(player: Entity, slot: ItemSlot): ItemInstance | undefined {
  const id = player.equipped?.[slot]
  return id ? findItem(player, id) : undefined
}

/** Fold every equipped item's affixes into the accumulator (recomputeMods). */
export function applyItemAffixes(player: Entity, mods: Mods): void {
  for (const slot of ['hat', 'weapon'] as ItemSlot[]) {
    const item = equippedItem(player, slot)
    if (!item) continue
    for (const roll of item.affixes) {
      AFFIX_DEFS[roll.id]?.apply(mods, roll.value)
    }
  }
}

// ── Display + comparison (playtest round 1: aligned rows, D4-style deltas) ──

/** One aligned display row per affix: label left, value right, always the
 * same shape so items scan as columns instead of prose. */
export interface AffixLine {
  id: string
  label: string
  /** "+25%" / "+1.2u" — the affix's own magnitude, signed. */
  value: string
  raw: number
}

/** AffixLine plus the against-what-you're-wearing diff. `delta` in raw
 * units (positive = improvement — affix values are higher-is-better by
 * construction); `deltaText` pre-formatted ("+10%" / "−0.3u"), null when
 * the values tie. */
export interface CompareLine extends AffixLine {
  delta: number
  deltaText: string | null
}

export function affixLines(item: ItemInstance): AffixLine[] {
  const out: AffixLine[] = []
  for (const roll of item.affixes) {
    const def = AFFIX_DEFS[roll.id]
    if (!def) continue
    out.push({ id: roll.id, label: def.label, value: `+${def.fmt(roll.value)}`, raw: roll.value })
  }
  return out
}

/** Compare a candidate against what's worn in its slot: the candidate's rows
 * with per-stat deltas, then "you'd lose this" rows for affixes only the
 * worn item has (value shown as the loss). `current` undefined = bare slot,
 * every row is a pure gain. */
export function compareItem(candidate: ItemInstance, current: ItemInstance | undefined): CompareLine[] {
  const currentByid = new Map(current?.affixes.map(r => [r.id, r.value]) ?? [])
  const out: CompareLine[] = []
  for (const line of affixLines(candidate)) {
    const def = AFFIX_DEFS[line.id]!
    const theirs = currentByid.get(line.id) ?? 0
    const delta = line.raw - theirs
    out.push({
      ...line,
      delta,
      deltaText: Math.abs(delta) < 1e-9 ? null
        : delta > 0 ? `+${def.fmt(delta)}` : `−${def.fmt(-delta)}`,
    })
    currentByid.delete(line.id)
  }
  // Worn-only affixes: equipping the candidate loses these outright.
  for (const [id, value] of currentByid) {
    const def = AFFIX_DEFS[id]
    if (!def) continue
    out.push({ id, label: def.label, value: '—', raw: 0, delta: -value, deltaText: `−${def.fmt(value)}` })
  }
  return out
}

/** Plain-text lines ("Guard +25%") — dev console / logs. */
export function describeItem(item: ItemInstance): string[] {
  return affixLines(item).map(l => `${l.label} ${l.value}`)
}
